import { Component, OnInit, Signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ModalController } from '@ionic/angular';
import {
  StateService,
  ToolService,
  RouterService,
  Machine,
  NodeItem,
} from 'projects/shared/src/public-api';
import { concatMap, from, Subscription, switchMap, tap } from 'rxjs';
import { UploadDocumentRequest } from '../../models/upload-document-reqest';

@Component({
  selector: 'lib-machine-form',
  templateUrl: './machine-form.component.html',
  styleUrls: ['./machine-form.component.scss'],
  standalone: false,
})
export class MachineFormComponent implements OnInit {
  constructor(
    public state: StateService,
    public tools: ToolService,
    public routerService: RouterService,
    public modalCtrl: ModalController
  ) {}

  subs: Subscription[] = [];
  paramId: string;
  machine: Machine = {};
  allMachines: Machine[] = [];
  showDuplicateInventoryNumberError: boolean = false;
  showDatePicker: boolean = false;
  showChangeImageButton: boolean = true;
  profilePicture: Blob | File | null;
  profilePictureNodeItem: NodeItem;
  machineDocuments: NodeItem[] = [];
  profilePictureUploadRequest: UploadDocumentRequest;
  filesToUpload: UploadDocumentRequest[] = [];
  filesToDelete: string[] = [];
  private uniqueId: string;

  ngOnInit() {
    this.subs.push(
      this.state.machines$.subscribe((x) => {
        this.allMachines = x;
        if (this.paramId != null) {
          this.machine = JSON.parse(
            JSON.stringify(x.find((y) => y.id == this.paramId))
          );
        } else {
          this.initNewForm();
        }
      })
    );
    this.subs.push(
      this.state.nodeItems$.subscribe((x) => {
        this.machineDocuments = x.filter(
          (x) => x.machineId == this.paramId && !x.isProfilePicture
        );
        this.profilePictureNodeItem = x.find(
          (x) => x.machineId === this.paramId && x.isProfilePicture
        );
        if (this.profilePictureNodeItem) {
          this.state
            .downloadBinary(this.profilePictureNodeItem.id, 'document')
            .subscribe((content) => {
              this.profilePicture = content;
            });
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subs?.forEach((x) => {
      x.unsubscribe();
    });
  }

  handleInventoryNumberInput(event: any) {
    let inventoryNumber = event.target.value as string;
    this.showDuplicateInventoryNumberError = false;
    let duplicate = this.allMachines.find(
      (x) =>
        x.inventoryNumber.toLocaleLowerCase().trim() ==
        inventoryNumber.toLocaleLowerCase().trim()
    );
    if (duplicate != null && duplicate.id != this.machine.id)
      this.showDuplicateInventoryNumberError = true;
  }

  cancel() {
    return this.modalCtrl.dismiss(null, 'cancel');
  }

  initNewForm() {
    this.machine.id = '';
    this.machine.category = '';
    this.machine.constructionYear = '';
    this.machine.inventoryNumber = '';
    this.machine.manufacturer = '';
    this.machine.serialNumber = '';
    this.machine.type = '';
    this.machine.inspectionFree = false;
    this.machine.notes = '';
  }

  dateChanged(evt: string) {
    this.machine.nextManualInspection = evt;
  }

  async create() {
    await this.modalCtrl.dismiss(null, 'confirm');
    this.state.createMachine(this.machine);
  }

  async update() {
    this.filesToUpload.push(this.profilePictureUploadRequest);
    this.uploadFiles(this.filesToUpload);
    if (this.filesToDelete?.length > 0)
      await this.state.deleteNodes(this.filesToDelete, false);
    this.state.updateMachine(this.machine);
    await this.modalCtrl.dismiss(null, 'confirm');
  }

  anyFieldInvalid(): boolean {
    if (!this.machine.category) return true;
    if (!this.machine.constructionYear) return true;
    if (!this.machine.inventoryNumber) return true;
    if (!this.machine.manufacturer) return true;
    if (!this.machine.serialNumber) return true;
    if (!this.machine.type) return true;
    if (this.showDuplicateInventoryNumberError) return true;
    return false;
  }

  public removeManualInspection() {
    this.showDatePicker = false;
    this.machine.nextManualInspection = null;
  }

  public openImageInput() {
    document.getElementById('profilePicture').click();
  }

  public openFileInput() {
    document.getElementById(this.uniqueId).click();
  }

  public setUploadFiles(
    uploadRequests: UploadDocumentRequest[],
    isProfilePicture: boolean
  ) {
    if (isProfilePicture) {
      this.profilePictureUploadRequest = uploadRequests[0];
      this.profilePicture = uploadRequests[0].file;
    } else {
      this.filesToUpload = uploadRequests;
    }
  }

  public async openFile() {
    if (!this.profilePictureNodeItem) return;
    await this.state.openFile(this.profilePictureNodeItem.id, 'document', true);
  }

  public deleteFile(id: string) {
    this.filesToDelete.push(id);
  }

  private uploadFiles(uploadRequests: UploadDocumentRequest[]) {
    from(uploadRequests)
      .pipe(
        concatMap((req) =>
          this.state
            .uploadFile(req)
            .pipe(
              tap(() =>
                this.tools.showInfoToast(
                  `Upload von ${req.displayName} erfolgreich!`
                )
              )
            )
        )
      )
      .subscribe({
        next: (x) => {
          this.tools.showInfoToast(`Upload erfolgreich!`);
        },
        error: (err) => {
          this.state.handleError(err);
        },
      });
  }

  public setUniqueId(id: string) {
    this.uniqueId = id;
  }
}
